//@ts-check
import cm from "../messages/common.js";
import { getString } from "./langutils.js";

// Cache the detected locale to avoid repeated detection
/** @type {Intl.Locale | null} */
let cachedLocale = null;

/**
 * Gets the locale state with merged common and page-specific messages
 * @param {*} messages - Page-specific messages
 * @returns {*} Locale state with locale and merged messages
 */
export function getLocaleState(messages) {
  try {
    const locale = getLocale();
    const mergedMessages = mergeCommonMessages(locale, messages);
    const localeState = {
      locale,
      messages: mergedMessages,
    };
    console.info("Locale state:", localeState);
    return localeState;
  } catch (e) {
    console.error("Error setting up locale:", e);
    // Return fallback state instead of undefined
    return {
      locale: new Intl.Locale("en-US"),
      messages: {},
    };
  }
}

/**
 * Detects and caches the user's locale from browser settings
 * @returns {Intl.Locale} The detected locale
 */
function getLocale() {
  // Return cached locale if already detected
  if (cachedLocale) {
    return cachedLocale;
  }

  try {
    // Use navigator.languages (array) with fallback to navigator.language
    const navLanguages = navigator.languages?.length > 0 ? navigator.languages : [navigator.language ?? "en-US"];

    cachedLocale = new Intl.Locale(navLanguages[0]);
    console.info(`Locale detected: ${cachedLocale.baseName}`, cachedLocale);
    return cachedLocale;
  } catch (e) {
    console.error("Locale detection failed:", e);
    // Fallback to en-US on error
    cachedLocale = new Intl.Locale("en-US");
    return cachedLocale;
  }
}

/**
 * Creates a translation function bound to the locale state
 * @param {*} options - Options containing localeState
 * @returns {Function} Translation function
 */
export function getTranslationsFunction(options) {
  const { localeState } = options;
  /**
   * @param {*} key
   * @param {*} defaultStr
   * @param {*} beautify
   */
  return (key, defaultStr = null, beautify = null) => {
    return getString(localeState.messages, key, defaultStr, beautify);
  };
}

/**
 * Finds messages for a locale with fallback chain:
 * 1. Try full locale (e.g., "es-MX")
 * 2. Try language only (e.g., "es")
 * 3. Fall back to "en-US"
 *
 * @param {*} messageSet - The message object to search
 * @param {Intl.Locale} locale - The locale to look up
 * @returns {*} - The messages for the locale
 */
function findMessagesForLocale(messageSet, locale) {
  // Try full locale with region first (e.g., "es-MX")
  const fullLocaleKey = locale.region ? `${locale.language}-${locale.region}` : null;

  if (fullLocaleKey) {
    // @ts-ignore - Dynamic locale key access
    const fullMessages = messageSet[fullLocaleKey];
    if (fullMessages) {
      return fullMessages;
    }
  }

  // Try language-only fallback (e.g., "es")
  // @ts-ignore - Dynamic locale key access
  const langMessages = messageSet[locale.language];
  if (langMessages) {
    return langMessages;
  }

  // Final fallback to en-US
  // @ts-ignore - Dynamic locale key access
  return messageSet["en-US"] || {};
}

/**
 * @param {Intl.Locale} locale
 * @param {*} msgs
 * @returns {*}
 */
function mergeCommonMessages(locale, msgs) {
  const lcm = findMessagesForLocale(cm, locale);
  const lmm = findMessagesForLocale(msgs, locale);

  // Page messages (lmm) should override common messages (lcm)
  return { ...lcm, ...lmm };
}
